import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import SEO from "../components/SEO";
import { Emblem } from "../components/Emblem";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("La contraseña debe tener al menos 6 caracteres."); return; }
    if (password !== confirm) { setErr("Las contraseñas no coinciden."); return; }
    if (!token) { setErr("Token no válido. Solicita un nuevo enlace."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-ink relative overflow-hidden">
      <SEO title="Restablecer contraseña" path="/reset-password" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="fixed inset-0 bg-dot-grid pointer-events-none opacity-40" />

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative z-10">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2.5 mb-10 w-fit">
            <Emblem className="h-9 w-9" />
            <span className="font-display font-extrabold text-white tracking-tight">TestOpoGuardiaCivil</span>
          </Link>

          <h1 className="font-display text-4xl font-extrabold text-white tracking-tighter">Nueva contraseña</h1>
          <p className="mt-2 text-sm text-slate-400">Elige una contraseña segura para tu cuenta.</p>

          {err && <Alert variant="destructive" className="mt-6 border-red-900/50 bg-red-950/40 text-red-200" data-testid="reset-error"><AlertDescription>{err}</AlertDescription></Alert>}

          {success ? (
            <div className="mt-8 p-5 border border-emerald-700/30 bg-emerald-950/30 rounded-xl flex items-start gap-3" data-testid="reset-success">
              <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-emerald-200">Contraseña actualizada</div>
                <div className="text-xs text-emerald-400/80 mt-1">Te redirigimos al login en unos segundos...</div>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 font-mono">Nueva contraseña</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres"
                  className="mt-2 h-12 bg-ink-800 border-white/10 text-white placeholder:text-slate-600 focus:border-gcgold/40 focus:ring-gcgold/20 rounded-xl"
                  data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 font-mono">Confirmar contraseña</Label>
                <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 h-12 bg-ink-800 border-white/10 text-white placeholder:text-slate-600 focus:border-gcgold/40 focus:ring-gcgold/20 rounded-xl"
                  data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-12 bg-gcgold hover:bg-gcgold-light text-ink font-bold rounded-xl shadow-[0_0_40px_-8px_rgba(212,175,55,0.6)] transition-all"
                data-testid="reset-submit-button">
                {loading ? "Actualizando..." : "Cambiar contraseña"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
